import { useEffect, useRef, useCallback } from 'react';
import {
  Button,
  CircularProgress,
  Skeleton,
  styled,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Column, Row } from 'further-ui/components/Layout';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';

const Content = styled(Column)({
  width: '100%',
  height: '100%',
  maxHeight: '100%',
});

const StyledCol = styled(Column)(({ theme }) => ({
  background: theme.palette.background.default,
  height: '100%',
}));

const StyledIframe = styled('iframe')({
  width: '100%',
  height: '100%',
  border: 'none',
});

type Props = {
  open: boolean;
  onClose: () => void;
  vehicleSigningDocumentId: string;
  runPostEditTemplateChecks: () => void;
};

const retrySchedule = [
  4_000, 8_000, 12_000, 16_000, 20_000, 30_000, 40_000, 50_000, 60_000, 70_000,
  80_000, 90_000, 100_000, 110_000, 120_000, 130_000, 140_000, 150_000, 160_000,
  170_000, 180_000,
];

const EditSignatureLocations: React.FC<Props> = ({
  open,
  onClose,
  runPostEditTemplateChecks,
  vehicleSigningDocumentId,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { success, error } = useNotification();
  const queryClient = useQueryClient();
  const vehicleSigningDocumentIdRef = useRef(vehicleSigningDocumentId);
  const selectedVehicleIdRef = useRef(selectedVehicleId);

  // Keep refs up to date
  useEffect(() => {
    vehicleSigningDocumentIdRef.current = vehicleSigningDocumentId;
    selectedVehicleIdRef.current = selectedVehicleId;
  }, [vehicleSigningDocumentId, selectedVehicleId]);

  const isEnabled = !!selectedVehicleId && !!vehicleSigningDocumentId && open;

  const { data, isLoading, failureCount } =
    api.vehicleSigningDocument.getEmbeddedEditingLink.useQuery(
      {
        vehicleSigningDocumentId,
        vehicleId: selectedVehicleId ?? '',
      },
      {
        enabled: isEnabled,
        retry: retrySchedule.length,
        retryDelay: (attemptIndex) =>
          retrySchedule[attemptIndex] ??
          retrySchedule[retrySchedule.length - 1],
        refetchOnWindowFocus: false,
      },
    );

  // Remove query from cache on unmount to prevent refetches after invalidation
  useEffect(() => {
    return () => {
      if (selectedVehicleIdRef.current) {
        const queryKey = getQueryKey(
          api.vehicleSigningDocument.getEmbeddedEditingLink,
          {
            vehicleSigningDocumentId: vehicleSigningDocumentIdRef.current,
            vehicleId: selectedVehicleIdRef.current,
          },
          'query',
        );
        queryClient.removeQueries({ queryKey });
      }
    };
  }, [queryClient]);

  const handleSubmit = () => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow?.postMessage('onSaveAndCloseClick', '*');
    }
  };

  const onTemplateEditingCompleted = useCallback(() => {
    success('Signature location(s) saved successfully');
    runPostEditTemplateChecks();
    onClose();
  }, [success, runPostEditTemplateChecks, onClose]);

  useEffect(() => {
    const handleMessage = (params: MessageEvent<{ status: string } | null>) => {
      if (params.origin !== 'https://app-eu.boldsign.com') {
        return;
      }

      if (params.data?.status === 'onTemplateEditingCompleted') {
        onTemplateEditingCompleted();
      }
      if (params.data?.status === 'onTemplateEditingFailed') {
        error('Saving the signature location failed. Please try again later.');
        onClose();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onClose, error, onTemplateEditingCompleted]);

  if (!open || !selectedVehicleId) return null;

  return (
    <FullPageDrawer
      open={true}
      onClose={onClose}
      title="Edit signature locations"
      size="xl"
      actions={
        <Row centered spacing="sm" justify="space-between">
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmit}
          >
            Save signature location(s)
          </Button>
        </Row>
      }
    >
      <Content spacing="xl" fullWidth justify="center">
        {isLoading || !data?.embeddedEditingLink ? (
          <>
            {failureCount > 1 ? (
              <StyledCol centered fullWidth spacing="lg" justify="center">
                <Typography variant="body1">
                  We are still processing this document. It may take a minute or
                  two depending on the size of the document.
                </Typography>
                <Typography variant="body1">
                  The screen will update when the document is ready.
                </Typography>
                <CircularProgress size={24} />
              </StyledCol>
            ) : (
              <Skeleton variant="rectangular" width="100%" height="100%" />
            )}
          </>
        ) : (
          <StyledIframe
            ref={iframeRef}
            src={data.embeddedEditingLink}
            width="100%"
            height="100%"
          />
        )}
      </Content>
    </FullPageDrawer>
  );
};

export default EditSignatureLocations;
